<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6 print-hide">
      <h1 class="text-3xl font-bold text-gray-800">Student Results</h1>
      <button @click="printPage" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition">
        Print All Reports
      </button>
    </div>

    <!-- Loading, Error, and No Students States -->
    <div v-if="loading" class="text-center py-20">
      <p class="text-gray-600">Loading student results...</p>
    </div>
    <div v-else-if="error" class="text-center text-red-500 bg-red-50 p-6 rounded-lg">
      <p class="font-semibold">Could not load results.</p>
      <p>{{ error.message }}</p>
    </div>
    <div v-else-if="students.length === 0" class="text-center py-20">
      <p class="text-gray-600">No student results are available for this test yet.</p>
    </div>

    <!-- Main Content for Printing -->
    <div v-else ref="printSection">
      <div v-for="student in students" :key="student.id" class="student-section mb-12">
        <div class="student-info mb-4 text-left border-b-2 pb-4">
          <p class="text-xl font-bold">{{ student.name }}</p>
          <p><strong>Total Points:</strong> {{ student.points }} / {{ student.totalPoints }}</p>
          <p><strong>Final Mark:</strong> {{ student.mark ?? 'N/A' }}</p>
        </div>

        <div class="grid-container border border-gray-300 rounded-lg shadow">
          <!-- Grid Header -->
          <div class="grid-header grid grid-cols-[50px,1fr,1fr,60px] font-medium text-gray-700 bg-gray-100">
            <div class="py-2 px-4 border-b">#</div>
            <div class="py-2 px-4 border-b">Question</div>
            <div class="py-2 px-4 border-b">Answer & Justification</div>
            <div class="py-2 px-4 border-b">Points</div>
          </div>

          <!-- Grid Body -->
          <div class="grid-body">
            <div v-for="answer in student.answers" :key="answer.question" class="grid-row grid grid-cols-[50px,1fr,1fr,60px] border-t">
              <div class="py-2 px-4 font-bold">{{ getQuestionNumber(answer.question) }}</div>
              <div class="py-2 px-4 whitespace-pre-wrap">{{ answer.question || 'N/A' }}</div>
              <div class="py-2 px-4 whitespace-pre-wrap">
                <div class="font-semibold">Student's Answer:</div>
                <p class="mb-2">{{ answer.answer || 'No answer provided' }}</p>
                <div v-if="answer.justification" class="mt-2 pt-2 border-t border-gray-200">
                  <strong class="font-semibold">Justification:</strong> {{ answer.justification }}
                </div>
                <div v-if="answer.correctedText" class="mt-2">
                  <strong class="font-semibold">Corrected Text:</strong> {{ answer.correctedText }}
                </div>
              </div>
              <div class="py-2 px-4 text-center">{{ answer.points ?? 'N/A' }}</div>
            </div>
          </div>
        </div>

        <div class="page-break"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import axios from '../services/axiosInstance'

const students = ref([])
const originalQuestions = ref([])
const loading = ref(true)
const error = ref(null)
const route = useRoute()
const printSection = ref(null)

const getQuestionNumber = (questionText) => {
  const question = originalQuestions.value.find(q => q.question === questionText)
  return question ? question.number : '#'
}

onMounted(async () => {
  const testId = route.params.testId
  if (!testId) {
    error.value = new Error('Test ID is missing from the route.')
    loading.value = false
    return
  }
  try {
    const [studentsResponse, questionsResponse] = await Promise.all([
      axios.get(`/results/test/${testId}/students`),
      axios.get(`/tests/${testId}/questions/overview`)
    ])
    students.value = studentsResponse.data
    originalQuestions.value = questionsResponse.data
  } catch (err) {
    console.error('Error fetching data:', err)
    error.value = err
  } finally {
    loading.value = false
  }
})

// --- THIS IS THE CORRECTED PRINT METHOD ---
const printPage = () => {
  if (!printSection.value) return
  const printContent = printSection.value.innerHTML
  const newWindow = window.open('', '_blank')
  newWindow.document.write(`
    <html>
      <head>
        <title>Student Reports</title>
<style>
          /* Basic styles */
          body { font-family: Arial, sans-serif; margin: 1.5cm; font-size: 10pt; }
          .student-section { margin-bottom: 25px; }
          .student-info { border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 15px; }
          .page-break { page-break-after: always; }
          .print-hide { display: none; }
          strong, .font-semibold { font-weight: bold; }
          p { margin: 0 0 0.5em 0; }

          /* Core Grid styles for printing */
          .grid-container {
            /* No border on the main container */
          }
          .grid-header, .grid-row {
            display: grid;
            grid-template-columns: 50px 1fr 1fr 60px;
            border-bottom: 1px solid #ccc;
            border-left: 1px solid #ccc;
            border-right: 1px solid #ccc;
          }
          .grid-header {
            border-top: 1px solid #ccc;
            background-color: #f2f2f2;
          }
          .grid-header > div, .grid-row > div {
            padding: 8px;
            word-wrap: break-word;
            border-right: 1px solid #ccc;
          }
          .grid-header > div:last-child, .grid-row > div:last-child {
            border-right: none;
          }
          .text-center { text-align: center; }
          .whitespace-pre-wrap { white-space: pre-wrap; }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
    </html>
  `)
  newWindow.document.close()
  setTimeout(() => {
    newWindow.print()
    newWindow.close()
  }, 250)
}
</script>

<style scoped>
/* Scoped styles are for the browser view only */
.grid-header > div, .grid-row > div {
  padding: 0.5rem 1rem;
}

.grid-header > div:not(:last-child),
.grid-row > div:not(:last-child) {
  border-right: 1px solid #e5e7eb;
}

@media print {
  .print-hide { display: none; }
}
.page-break { display: none; }
</style>
